/**
 * ¿ES EL MISMO FOCO? — la regla que decide a qué foco se PROPONE unir un
 * muestreo cuando alguien confirma su plaga.
 *
 * Hasta ahora cada parte era su propia chincheta. Si otro trabajador volvía a
 * la misma línea una semana después, nacía una chincheta nueva sin relación con
 * la anterior, y el avance de esa plaga —que es lo que se quiere ver— no
 * existía en ningún sitio. Un FOCO es eso: los muestreos confirmados de la
 * misma plaga en el mismo sitio, a lo largo del tiempo.
 *
 * Decisiones del usuario (21-09-2026) que esta lib convierte en código:
 *
 *  · SOLO LO CONFIRMADO ENLAZA, y nunca a escondidas. Esto no enlaza nada:
 *    PROPONE. El caso claro llega relleno a la pantalla de confirmar; el dudoso
 *    obliga a elegir. Quien confirma siempre ve a qué se une.
 *  · MISMO SITIO = mismo sector y MISMA LÍNEA. Las líneas vecinas (±2) son
 *    caso dudoso: cubren que alguien apunte la 13 en vez de la 12, o que la
 *    plaga haya saltado a la de al lado. Más lejos, foco nuevo.
 *  · LA LÍNEA MANDA SOBRE EL GPS. Las líneas están a 1,5–3 m y el GPS real bajo
 *    plástico ronda ±21 m: no distingue una línea de la vecina. Lo único que el
 *    GPS sabe decir es a qué ALTURA de la línea estabas, y solo cuando el ancla
 *    es `gps_snap`. Un `line_midpoint` lleva un `t = 0,5` que nadie midió: jamás
 *    entra en una cuenta de distancia.
 *  · Una línea de 90 m puede tener dos focos. Misma línea pero a más de
 *    `SAME_LINE_SPLIT_M` —y con las DOS posiciones medidas— es caso dudoso.
 *  · OTRA PLAGA en la misma línea no se mezcla ni se ignora: se avisa.
 *  · REBROTE: un foco resuelto hace `REBOUND_WINDOW_DAYS` o menos se propone
 *    REABRIR; más viejo, es solo un antecedente. Y un foco no cruza de un ciclo
 *    de cultivo a otro: planta nueva, foco nuevo.
 *  · LA PISTA DEL TRABAJADOR NO ENLAZA. «Es lo mismo» ordena y resalta;
 *    «es otra cosa» impide que algo llegue relleno en su contra. Nada más.
 *
 * LOS NÚMEROS (±2 líneas, 30 m) no salen de la experiencia —solo hay un caso
 * real de repetición en producción— sino del simulador de
 * `src/__tests__/focos/sim`, y quedan congelados por un guard. Por eso se
 * pueden sobrescribir desde `ctx.thresholds`: es la puerta del simulador, no
 * una opción de producto.
 *
 * PURA Y SIN RELOJ. Aquí no entra `new Date()`: las fechas son cadenas ISO
 * `yyyy-MM-dd` y los días se cuentan en UTC (`reports/dates`). El `addDays` del
 * modelo del mapa se equivoca un día en el cambio de hora de Madrid; una
 * ventana de 21 días no se puede permitir eso.
 *
 * La geometría vive SOLO aquí. La base de datos valida integridad (empresa,
 * sector, plaga, ciclo) pero no repite ni una de estas cuentas: dos copias de
 * una regla de distancia acaban siendo dos reglas.
 */
import { samePest } from "./pestCanon";
import { diffDaysIso } from "../reports/dates";
import { REBOUND_WINDOW_DAYS } from "../reports/constants";

/** Cuántas líneas a cada lado siguen siendo «puede que sea el mismo foco». */
export const NEIGHBOUR_LINES = 2;
/** Misma línea pero más lejos que esto, con las dos posiciones medidas: se pregunta. */
export const SAME_LINE_SPLIT_M = 30;
/**
 * Sin datos de ciclo no se sabe si hay planta nueva entre dos muestreos. Hasta
 * este silencio se da el beneficio de la duda; más allá, el foco viejo es solo
 * un antecedente.
 */
export const CYCLE_UNKNOWN_MAX_GAP_DAYS = 120;
/** El único `anchor_source` cuya posición a lo largo de la línea se midió de verdad. */
export const PRECISE_SOURCE = "gps_snap";
/** Viaja en la telemetría de cada decisión: permite saber con qué regla se propuso. */
export const THRESHOLDS_VERSION = "2026-09-21.a";

export interface LinkThresholds {
  neighbourLines: number;
  sameLineSplitM: number;
  reboundWindowDays: number;
  cycleUnknownMaxGapDays: number;
  /**
   * Una línea vecina, pero en la otra punta: ¿sigue siendo candidata? La regla
   * aprobada no lo corta (`Infinity`). Está aquí para que el simulador pueda
   * medir si cortarlo ahorra preguntas sin partir focos.
   */
  neighbourFarAlongM: number;
  /**
   * Cuántos metros de la precisión declarada se le perdonan a cada posición
   * antes de comparar. `0` = no se perdona nada, que es la regla aprobada.
   */
  accuracyCreditCapM: number;
}

export const DEFAULT_THRESHOLDS: LinkThresholds = {
  neighbourLines: NEIGHBOUR_LINES,
  sameLineSplitM: SAME_LINE_SPLIT_M,
  reboundWindowDays: REBOUND_WINDOW_DAYS,
  cycleUnknownMaxGapDays: CYCLE_UNKNOWN_MAX_GAP_DAYS,
  neighbourFarAlongM: Number.POSITIVE_INFINITY,
  accuracyCreditCapM: 0,
};

export type LinkTier = "clear" | "ambiguous" | "rebound" | "other_pest";

export type LinkReason =
  | "same_row"
  | "same_line_number"
  | "neighbour_line"
  | "far_along_line"
  | "sampling_without_row"
  | "focus_without_row"
  | "row_inactive"
  | "multiple_clear"
  | "cycle_unknown"
  | "resolved_recently"
  | "pest_differs"
  | "hint_same"
  | "hint_from_task"
  | "hint_different";

export type FollowUpAnswer = "same" | "different" | "unknown";

/** Lo que dijo quien estaba delante de la planta. Una pista, nunca un enlace. */
export interface LinkHint {
  focusId: string | null;
  answer: FollowUpAnswer | null;
  /** El parte nació de una tarea de seguimiento de ese foco. */
  fromTask: boolean;
}

/** El muestreo que se está confirmando, reducido a lo que la regla necesita. */
export interface LinkSampling {
  id: string | null;
  blockId: string;
  rowId: string | null;
  lineNumber: number | null;
  /** Metros desde el extremo `a` de la línea. `null` si la posición no se midió. */
  alongM: number | null;
  anchorSource: string | null;
  accuracyM?: number | null;
  /** Clave canónica de la plaga CONFIRMADA (ver `pestCanon`). Sin plaga no hay propuesta. */
  pestKey: string | null;
  /** Fecha del muestreo, `yyyy-MM-dd`. */
  date: string;
  cropCycleId: string | null;
  hint?: LinkHint | null;
}

export interface PrecisePosition {
  alongM: number;
  accuracyM: number | null;
}

/** Una línea que el foco ocupa: la de alguno de sus muestreos. */
export interface FocusLine {
  rowId: string | null;
  lineNumber: number | null;
  /** Sigue activa en el diseño publicado. */
  active: boolean;
}

/** Un foco existente, resumido. Lo construye `summarizeFocus`. */
export interface FocusSummary {
  id: string;
  blockId: string;
  /** La línea de ORIGEN: la que se enseña («Trips · L12»). Para comparar sitios se usa `lines`. */
  rowId: string | null;
  lineNumber: number | null;
  /** La línea de origen sigue activa en el diseño publicado. */
  rowActive: boolean;
  /**
   * TODAS las líneas que ocupa, una por cada línea distinta de sus muestreos.
   *
   * Un brote no se queda en la línea donde se vio primero: salta a la de al
   * lado. Si quien confirma unió al foco de la 12 un parte de la 13, el foco
   * ocupa la 12 y la 13, y un parte en la 15 es vecino SUYO aunque esté a tres
   * de donde empezó. Medir solo contra el origen partía los brotes que más
   * importan, los que se extienden (lo enseñó el simulador).
   *
   * Vacío ⇒ vale la línea de origen.
   */
  lines: readonly FocusLine[];
  /** Posiciones MEDIDAS (`gps_snap`) de sus muestreos a lo largo de la línea. */
  precise: PrecisePosition[];
  pestKey: string | null;
  pestName: string | null;
  /** Manda el muestreo más reciente (decisión 21-09-2026): resuelto él, resuelto el foco. */
  status: "open" | "resolved";
  /** Día en que se resolvió, `yyyy-MM-dd`. `null` si está abierto. */
  resolvedOn: string | null;
  firstDate: string;
  lastDate: string;
  sampleCount: number;
  lastSeverity: string | null;
  cropCycleId: string | null;
  lastPhotoUrl?: string | null;
}

export interface LinkContext {
  /**
   * Números de línea ACTIVOS de cada sector, ordenados. La separación entre dos
   * líneas se mide en posiciones, no restando números: en un sector numerado
   * 1, 2, 5, 6 la línea 2 y la 5 son vecinas.
   */
  lineOrdinals?: Map<string, readonly number[]>;
  /**
   * Fechas de inicio de los ciclos de cultivo de cada sector. Los partes casi
   * nunca llevan `crop_cycle_id` (el asistente de captura no lo pone), así que
   * la frontera se detecta por fechas: un inicio de ciclo ENTRE el último
   * muestreo del foco y este es planta nueva.
   */
  cycleStarts?: Map<string, readonly string[]>;
  thresholds?: Partial<LinkThresholds>;
}

export interface Candidate {
  focus: FocusSummary;
  tier: LinkTier;
  reasons: LinkReason[];
  /** Líneas de separación (0 = la misma). `null` si alguno de los dos no tiene línea. */
  lineGap: number | null;
  /** Metros de separación a lo largo de la línea. `null` si no se midieron las dos posiciones. */
  alongGapM: number | null;
  /** Días desde el último muestreo del foco hasta este (negativo si este es anterior). */
  daysSinceLast: number;
  /** Días desde que se resolvió. `null` si está abierto o este muestreo es anterior. */
  daysSinceResolved: number | null;
  /** El trabajador (o la tarea de seguimiento) señaló ESTE foco. */
  hinted: boolean;
}

export type LinkProposal =
  /** Caso claro: llega relleno y se puede cambiar. */
  | { kind: "join"; focusId: string }
  /** Foco resuelto hace poco: se propone reabrirlo como rebrote. */
  | { kind: "reopen"; focusId: string }
  /** Caso dudoso: quien confirma TIENE que elegir entre estos o «foco nuevo». */
  | { kind: "must_choose"; focusIds: string[] }
  /** Hay un foco abierto de OTRA plaga en esta línea: avisar y elegir. */
  | { kind: "other_pest"; focusId: string }
  | { kind: "new" };

export interface LinkResult {
  candidates: Candidate[];
  /** Focos que no son candidatos pero cuentan la historia del sitio. */
  antecedents: FocusSummary[];
  proposal: LinkProposal;
}

const TIER_ORDER: Record<LinkTier, number> = { clear: 0, ambiguous: 1, rebound: 2, other_pest: 3 };

const thresholdsOf = (ctx: LinkContext): LinkThresholds => ({ ...DEFAULT_THRESHOLDS, ...(ctx.thresholds ?? {}) });

const finite = (n: number | null | undefined): n is number => typeof n === "number" && Number.isFinite(n);

type CycleRelation = "same" | "other" | "unknown";

/**
 * ¿Hay planta nueva entre el foco y este muestreo?
 *
 * Con los dos ids, mandan los ids. Sin ellos —que es lo normal— se busca un
 * inicio de ciclo entre las dos fechas. Y si el sector no tiene ciclos
 * registrados, no se sabe: `unknown`, que se resuelve con el silencio máximo.
 */
function cycleRelation(s: LinkSampling, f: FocusSummary, ctx: LinkContext): CycleRelation {
  if (s.cropCycleId && f.cropCycleId) return s.cropCycleId === f.cropCycleId ? "same" : "other";
  const starts = ctx.cycleStarts?.get(s.blockId);
  if (!starts || starts.length === 0) return "unknown";
  const lo = f.lastDate < s.date ? f.lastDate : s.date;
  const hi = f.lastDate < s.date ? s.date : f.lastDate;
  return starts.some((d) => d > lo && d <= hi) ? "other" : "same";
}

type Place = Pick<LinkSampling, "blockId" | "rowId" | "lineNumber">;

/** Separación en líneas entre un sitio y UNA línea. `null` si a alguno le falta la línea. */
function gapToLine(s: Place, line: FocusLine, ctx: LinkContext): number | null {
  if (s.rowId && line.rowId && s.rowId === line.rowId) return 0;
  if (!finite(s.lineNumber) || !finite(line.lineNumber)) return null;
  if (s.lineNumber === line.lineNumber) return 0;
  const ordinals = ctx.lineOrdinals?.get(s.blockId);
  if (ordinals) {
    const i = ordinals.indexOf(s.lineNumber);
    const j = ordinals.indexOf(line.lineNumber);
    if (i >= 0 && j >= 0) return Math.abs(i - j);
  }
  return Math.abs(s.lineNumber - line.lineNumber);
}

interface Nearest {
  /** Separación a la línea más cercana del foco. `null` si no hay ninguna comparable. */
  gap: number | null;
  /** Esa línea sigue activa. */
  active: boolean;
  /** Coincide por id de fila, y no solo por número. */
  byRowId: boolean;
}

/**
 * La línea del foco MÁS CERCANA al sitio. A igual separación gana la que sigue
 * activa: un foco con un pie en una línea desactivada y otro en una viva no es
 * un foco dudoso por la primera.
 */
function nearestLine(s: Place, f: FocusSummary, ctx: LinkContext): Nearest {
  const lines: readonly FocusLine[] =
    f.lines.length > 0 ? f.lines : [{ rowId: f.rowId, lineNumber: f.lineNumber, active: f.rowActive }];
  let best: Nearest = { gap: null, active: f.rowActive, byRowId: false };
  for (const line of lines) {
    const gap = gapToLine(s, line, ctx);
    if (gap == null) continue;
    if (best.gap == null || gap < best.gap || (gap === best.gap && line.active && !best.active)) {
      best = { gap, active: line.active, byRowId: s.rowId != null && s.rowId === line.rowId };
    }
  }
  return best;
}

/**
 * Metros entre este muestreo y el miembro medido más cercano del foco.
 *
 * Solo existe si LAS DOS posiciones se midieron. Basta que una sea un punto
 * medio inventado para que la respuesta sea «no se sabe», que es muy distinto
 * de «están lejos»: un «no se sabe» nunca parte un foco.
 */
function alongGapOf(s: LinkSampling, f: FocusSummary, T: LinkThresholds): number | null {
  if (s.anchorSource !== PRECISE_SOURCE || !finite(s.alongM)) return null;
  let best: number | null = null;
  for (const p of f.precise) {
    if (!finite(p.alongM)) continue;
    const credit =
      T.accuracyCreditCapM > 0
        ? Math.min(T.accuracyCreditCapM, finite(s.accuracyM) ? s.accuracyM : 0) +
          Math.min(T.accuracyCreditCapM, finite(p.accuracyM) ? p.accuracyM : 0)
        : 0;
    const gap = Math.max(0, Math.abs(s.alongM - p.alongM) - credit);
    if (best === null || gap < best) best = gap;
  }
  return best;
}

/**
 * Para la regla, un foco está «abierto» también si este muestreo es ANTERIOR a
 * su resolución: un parte que llega tarde pertenece a cuando el foco vivía, y
 * unirlo no reabre nada —manda el más reciente—.
 *
 * El MISMO día cuenta como posterior: con fechas sin hora no se puede saber qué
 * fue antes, y entre dar por bueno un tratamiento que quizá no bastó y señalar
 * un rebrote de día cero, lo segundo es lo que no esconde nada.
 */
function isOpenFor(s: LinkSampling, f: FocusSummary): boolean {
  if (f.status === "open") return true;
  return f.resolvedOn != null && s.date < f.resolvedOn;
}

function compareCandidates(a: Candidate, b: Candidate): number {
  const tier = TIER_ORDER[a.tier] - TIER_ORDER[b.tier];
  if (tier !== 0) return tier;
  if (a.hinted !== b.hinted) return a.hinted ? -1 : 1;
  const ga = a.lineGap ?? Number.POSITIVE_INFINITY;
  const gb = b.lineGap ?? Number.POSITIVE_INFINITY;
  if (ga !== gb) return ga - gb;
  const aa = a.alongGapM ?? Number.POSITIVE_INFINITY;
  const ab = b.alongGapM ?? Number.POSITIVE_INFINITY;
  if (aa !== ab) return aa - ab;
  if (a.focus.lastDate !== b.focus.lastDate) return a.focus.lastDate < b.focus.lastDate ? 1 : -1;
  if (a.focus.sampleCount !== b.focus.sampleCount) return b.focus.sampleCount - a.focus.sampleCount;
  return a.focus.id < b.focus.id ? -1 : a.focus.id > b.focus.id ? 1 : 0;
}

interface Ranked {
  candidates: Candidate[];
  antecedents: FocusSummary[];
}

function rank(s: LinkSampling, foci: readonly FocusSummary[], ctx: LinkContext): Ranked {
  const candidates: Candidate[] = [];
  const antecedents: FocusSummary[] = [];
  // Sin plaga confirmada no hay nada que proponer: `null` no casa ni con `null`.
  if (s.pestKey == null) return { candidates, antecedents };
  const T = thresholdsOf(ctx);
  const hint = s.hint ?? null;

  for (const f of foci) {
    if (f.blockId !== s.blockId) continue;
    const near = nearestLine(s, f, ctx);
    const lineGap = near.gap;
    const pestSame = samePest(s.pestKey, f.pestKey);
    const daysSinceLast = diffDaysIso(f.lastDate, s.date);

    const cycle = cycleRelation(s, f, ctx);
    const silent = Math.abs(daysSinceLast) > T.cycleUnknownMaxGapDays;
    if (cycle === "other" || (cycle === "unknown" && silent)) {
      if (pestSame && lineGap === 0) antecedents.push(f);
      continue;
    }

    const hinted = hint?.focusId != null && hint.focusId === f.id && (hint.answer === "same" || hint.fromTask);
    const reasons: LinkReason[] = [];
    if (cycle === "unknown") reasons.push("cycle_unknown");
    if (hinted) reasons.push(hint?.fromTask ? "hint_from_task" : "hint_same");
    if (hint?.focusId === f.id && hint.answer === "different") reasons.push("hint_different");

    const open = isOpenFor(s, f);
    const alongGapM = alongGapOf(s, f, T);
    const daysSinceResolved = !open && f.resolvedOn != null ? diffDaysIso(f.resolvedOn, s.date) : null;
    const push = (tier: LinkTier, ...more: LinkReason[]) =>
      candidates.push({
        focus: f,
        tier,
        reasons: [...more, ...reasons],
        lineGap,
        alongGapM,
        daysSinceLast,
        daysSinceResolved,
        hinted,
      });

    if (pestSame) {
      if (open) {
        if (lineGap === 0) {
          const sameRow = near.byRowId ? "same_row" : "same_line_number";
          if (!near.active) push("ambiguous", sameRow, "row_inactive");
          else if (alongGapM != null && alongGapM > T.sameLineSplitM) push("ambiguous", sameRow, "far_along_line");
          else push("clear", sameRow);
        } else if (lineGap == null) {
          push("ambiguous", s.rowId == null && !finite(s.lineNumber) ? "sampling_without_row" : "focus_without_row");
        } else if (lineGap <= T.neighbourLines) {
          if (alongGapM == null || alongGapM <= T.neighbourFarAlongM) push("ambiguous", "neighbour_line");
          else if (hinted) push("ambiguous", "neighbour_line", "far_along_line");
        } else if (hinted) {
          // El trabajador señaló ESTE foco: no se le enmienda la plana sin enseñarlo.
          push("ambiguous");
        }
      } else if ((lineGap === 0 || lineGap == null) && daysSinceResolved != null) {
        /* SIN LÍNEA TAMBIÉN, pero preguntando. La repetición en seco con los
           partes de la demo lo enseñó: nueve partes de araña roja en un sector
           sin líneas nacían como nueve focos sin que nadie preguntara nada,
           porque el rebrote exigía «misma línea» y ahí no hay línea que
           comparar. Donde el sector es el sitio más fino que se conoce, no
           saber la línea no es «está lejos»: es «no lo sé», y eso se pregunta.
           Nunca llega relleno (ver `cleanRebound`). */
        if (daysSinceResolved <= T.reboundWindowDays) {
          const more: LinkReason[] = ["resolved_recently"];
          if (lineGap == null) more.push(s.rowId == null && !finite(s.lineNumber) ? "sampling_without_row" : "focus_without_row");
          if (alongGapM != null && alongGapM > T.sameLineSplitM) more.push("far_along_line");
          if (!near.active) more.push("row_inactive");
          push("rebound", ...more);
        } else if (lineGap === 0) {
          antecedents.push(f);
        }
      }
      continue;
    }

    // Otra plaga: solo se avisa si las dos se conocen, el foco vive y es ESTA línea.
    if (f.pestKey != null && open && lineGap === 0) push("other_pest", "pest_differs");
  }

  /* Dos focos «claros» a la vez no son claros: en esa línea hay dos candidatos
     igual de buenos y elegir uno por orden alfabético sería enlazar a ciegas. */
  const clear = candidates.filter((c) => c.tier === "clear");
  if (clear.length > 1) {
    for (const c of clear) {
      c.tier = "ambiguous";
      c.reasons = ["multiple_clear", ...c.reasons];
    }
  }

  candidates.sort(compareCandidates);
  antecedents.sort((a, b) => (a.lastDate !== b.lastDate ? (a.lastDate < b.lastDate ? 1 : -1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return { candidates, antecedents };
}

/** Los focos a los que este muestreo se podría unir, del mejor al peor. Orden total y estable. */
export function rankFocusCandidates(
  sampling: LinkSampling,
  foci: readonly FocusSummary[],
  ctx: LinkContext = {},
): Candidate[] {
  return rank(sampling, foci, ctx).candidates;
}

/**
 * Qué se le enseña a quien confirma.
 *
 * `join` y `reopen` llegan rellenos —y se pueden cambiar—. Todo lo demás con
 * candidatos obliga a elegir. Una pista «es otra cosa» sobre el caso claro lo
 * baja a elección: nada llega relleno en contra de quien estuvo allí.
 */
export function proposeLink(sampling: LinkSampling, foci: readonly FocusSummary[], ctx: LinkContext = {}): LinkResult {
  const { candidates, antecedents } = rank(sampling, foci, ctx);
  const of = (tier: LinkTier) => candidates.filter((c) => c.tier === tier);
  const clear = of("clear");
  const ambiguous = of("ambiguous");
  const rebound = of("rebound");
  const otherPest = of("other_pest");
  const joinable = [...clear, ...ambiguous, ...rebound].map((c) => c.focus.id);

  let proposal: LinkProposal;
  if (clear.length === 1 && !clear[0].reasons.includes("hint_different")) {
    proposal = { kind: "join", focusId: clear[0].focus.id };
  } else if (clear.length > 0 || ambiguous.length > 0) {
    proposal = { kind: "must_choose", focusIds: joinable };
  } else if (rebound.length === 1 && cleanRebound(rebound[0])) {
    proposal = { kind: "reopen", focusId: rebound[0].focus.id };
  } else if (rebound.length > 0) {
    proposal = { kind: "must_choose", focusIds: joinable };
  } else if (otherPest.length > 0) {
    proposal = { kind: "other_pest", focusId: otherPest[0].focus.id };
  } else {
    proposal = { kind: "new" };
  }
  return { candidates, antecedents, proposal };
}

/** Un rebrote solo llega relleno si no hay NADA dudoso en él: su línea, conocida, activa y cerca. */
const cleanRebound = (c: Candidate) =>
  c.lineGap === 0 &&
  !c.reasons.includes("far_along_line") &&
  !c.reasons.includes("row_inactive") &&
  !c.reasons.includes("hint_different");

/** El sitio que ha elegido el trabajador, antes de que nadie sepa qué plaga es. */
export interface QuestionPlace {
  blockId: string;
  rowId: string | null;
  lineNumber: number | null;
  date: string;
  cropCycleId?: string | null;
}

/**
 * ¿Sobre qué foco se le pregunta al trabajador «¿es lo mismo que ves ahora?»?
 *
 * SIN MIRAR LA PLAGA: el trabajador no la elige y en ese momento nadie la sabe.
 * Solo su misma línea, solo focos vivos o resueltos hace poco, y uno solo —el
 * de actividad más reciente—: la captura tiene que seguir siendo rápida, y una
 * lista de focos es justo lo que un trabajador no tiene por qué entender.
 */
export function pickFocusForQuestion(
  place: QuestionPlace,
  foci: readonly FocusSummary[],
  ctx: LinkContext = {},
): FocusSummary | null {
  const T = thresholdsOf(ctx);
  const probe: LinkSampling = {
    id: null,
    blockId: place.blockId,
    rowId: place.rowId,
    lineNumber: place.lineNumber,
    alongM: null,
    anchorSource: null,
    pestKey: null,
    date: place.date,
    cropCycleId: place.cropCycleId ?? null,
  };
  let best: FocusSummary | null = null;
  for (const f of foci) {
    if (f.blockId !== place.blockId) continue;
    if (nearestLine(probe, f, ctx).gap !== 0) continue;
    const cycle = cycleRelation(probe, f, ctx);
    const silent = Math.abs(diffDaysIso(f.lastDate, place.date)) > T.cycleUnknownMaxGapDays;
    if (cycle === "other" || (cycle === "unknown" && silent)) continue;
    if (!isOpenFor(probe, f)) {
      if (f.resolvedOn == null || diffDaysIso(f.resolvedOn, place.date) > T.reboundWindowDays) continue;
    }
    if (
      best === null ||
      f.lastDate > best.lastDate ||
      (f.lastDate === best.lastDate &&
        (f.sampleCount > best.sampleCount || (f.sampleCount === best.sampleCount && f.id < best.id)))
    ) {
      best = f;
    }
  }
  return best;
}

/** Un miembro del foco, con lo justo para resumirlo. */
export interface FocusMember {
  id: string;
  /** Un muestreo con plaga, o una revisión limpia («ya no hay»). */
  kind: "sampling" | "clean_check";
  date: string;
  createdAt: string | null;
  rowId: string | null;
  lineNumber: number | null;
  /** Su línea sigue activa en el diseño publicado. Ausente ⇒ sí. */
  rowActive?: boolean;
  /** Posición 0..1 a lo largo de la línea, tal como se guardó. */
  anchorT: number | null;
  anchorSource: string | null;
  accuracyM: number | null;
  /** Largo de SU línea en metros (para pasar `anchorT` a metros). */
  rowLengthM: number | null;
  severity: string | null;
  resolved: boolean;
  /** Marca de tiempo de la resolución. Si falta y está resuelto, vale su propia fecha. */
  resolvedAt: string | null;
  photoUrl: string | null;
}

const byAge = (a: FocusMember, b: FocusMember): number => {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const ca = a.createdAt ?? "";
  const cb = b.createdAt ?? "";
  if (ca !== cb) return ca < cb ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/** Los muestreos de verdad; si el foco solo tuviera revisiones limpias, esas. */
const evidence = <T extends FocusMember>(members: readonly T[]): T[] => {
  const samplings = members.filter((m) => m.kind === "sampling");
  return samplings.length > 0 ? samplings : [...members];
};

/**
 * DÓNDE SE PINTA EL FOCO: en su primer muestreo MEDIDO; si ninguno lo está, en
 * el primero a secas.
 *
 * Se descartó «el más reciente» y «la media»: las dos mueven la chincheta cada
 * vez que llega un parte, y una chincheta que salta de sitio se lee como un
 * foco distinto. Con esta regla cambia como mucho UNA vez en su vida —cuando
 * llega la primera posición buena— y no depende de la fecha que se esté
 * mirando, así que la barra del histórico no la mueve.
 */
export function focusAnchorMember<T extends FocusMember>(members: readonly T[]): T | null {
  const sorted = evidence(members).sort(byAge);
  if (sorted.length === 0) return null;
  return sorted.find((m) => m.anchorSource === PRECISE_SOURCE) ?? sorted[0];
}

export interface FocusIdentity {
  id: string;
  blockId: string;
  rowId: string | null;
  lineNumber: number | null;
  rowActive: boolean;
  pestKey: string | null;
  pestName: string | null;
  cropCycleId: string | null;
}

/**
 * El resumen de un foco a partir de sus miembros. `null` si no le queda ninguno
 * —un foco vacío no existe: ni se propone ni se pinta—.
 */
export function summarizeFocus(identity: FocusIdentity, members: readonly FocusMember[]): FocusSummary | null {
  if (members.length === 0) return null;
  const sorted = [...members].sort(byAge);
  const samplings = evidence(sorted);
  const latest = samplings[samplings.length - 1];
  const resolved = latest.resolved;
  const precise: PrecisePosition[] = [];
  for (const m of samplings) {
    if (m.anchorSource !== PRECISE_SOURCE) continue;
    if (!finite(m.anchorT) || !finite(m.rowLengthM) || m.rowLengthM <= 0) continue;
    precise.push({ alongM: m.anchorT * m.rowLengthM, accuracyM: finite(m.accuracyM) ? m.accuracyM : null });
  }
  let lastPhotoUrl: string | null = null;
  for (let i = sorted.length - 1; i >= 0 && lastPhotoUrl === null; i--) lastPhotoUrl = sorted[i].photoUrl;
  /* Las líneas que ocupa: la de origen y la de cada muestreo, sin repetir. Una
     revisión limpia no ocupa nada: dice dónde NO hay plaga. */
  const lines: FocusLine[] = [];
  const seen = new Set<string>();
  const occupy = (rowId: string | null, lineNumber: number | null, active: boolean) => {
    if (rowId == null && !finite(lineNumber)) return;
    const key = rowId ?? `#${lineNumber}`;
    if (seen.has(key)) return;
    seen.add(key);
    lines.push({ rowId, lineNumber: finite(lineNumber) ? lineNumber : null, active });
  };
  occupy(identity.rowId, identity.lineNumber, identity.rowActive);
  for (const m of samplings) occupy(m.rowId, m.lineNumber, m.rowActive ?? true);
  return {
    id: identity.id,
    blockId: identity.blockId,
    rowId: identity.rowId,
    lineNumber: identity.lineNumber,
    rowActive: identity.rowActive,
    lines,
    precise,
    pestKey: identity.pestKey,
    pestName: identity.pestName,
    status: resolved ? "resolved" : "open",
    resolvedOn: resolved ? (latest.resolvedAt ?? latest.date).slice(0, 10) : null,
    firstDate: sorted[0].date,
    lastDate: sorted[sorted.length - 1].date,
    sampleCount: samplings.length,
    lastSeverity: latest.severity,
    cropCycleId: identity.cropCycleId,
    lastPhotoUrl,
  };
}
